import type { TeactNode } from '../../lib/teact/teact';
import React, { memo, useEffect, useMemo, useRef, useState } from '../../lib/teact/teact';

import type { IAnchorPosition } from '../../global/types';
import type { Layout } from '../../hooks/useMenuPosition';

import { requestForcedReflow } from '../../lib/fasterdom/fasterdom';
import buildClassName from '../../util/buildClassName';

import useFlag from '../../hooks/useFlag';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import DropdownItemContent from './DropdownItemContent';
import DropdownMenu from './DropdownMenu';
import Spinner from './Spinner';

import styles from './Dropdown.module.scss';

export interface DropdownItem<T extends string = string> {
  value: T;
  name: string;
  selectedName?: string;
  description?: string | TeactNode;
  icon?: string | TeactNode | (() => TeactNode);
  iconClassName?: string;
  overlayIcon?: string;
  fontIcon?: string;
  fontIconClassName?: string;
  isDisabled?: boolean;
  isDangerous?: boolean;
  isTelemetryBlocked?: boolean;
  withDelimiter?: boolean;
  withDelimiterAfter?: boolean;
  noTranslate?: boolean;
}

interface OwnProps<T extends string> {
  label?: string;
  selectedValue?: T;
  items: DropdownItem<T>[];
  className?: string;
  labelClassName?: string;
  itemClassName?: string;
  buttonClassName?: string;
  buttonIconClassName?: string;
  buttonIconOverlayClassName?: string;
  menuClassName?: string;
  theme?: 'light' | 'inherit';
  arrow?: 'caret' | 'chevron' | 'expand';
  menuPositionX?: 'right' | 'left';
  menuPositionY?: 'top' | 'bottom';
  withCustomScrollbar?: boolean;
  withPortal?: boolean;
  disabled?: boolean;
  shouldTranslateOptions?: boolean;
  onChange?: (value: T) => void;
  isLoading?: boolean;
  buttonPrefix?: TeactNode;
}

const ARROW_ICONS = {
  caret: 'icon-caret-down',
  chevron: 'icon-chevron-down',
  expand: 'icon-expand',
};

const DEFAULT_ARROW = 'caret';
const DEFAULT_MENU_POSITION_X = 'right';

function Dropdown<T extends string>({
  label,
  items,
  selectedValue,
  className,
  labelClassName,
  itemClassName,
  buttonClassName,
  buttonIconClassName,
  buttonIconOverlayClassName,
  menuClassName,
  theme,
  arrow = DEFAULT_ARROW,
  menuPositionX = DEFAULT_MENU_POSITION_X,
  menuPositionY,
  withCustomScrollbar,
  withPortal,
  disabled,
  shouldTranslateOptions,
  onChange,
  isLoading = false,
  buttonPrefix,
}: OwnProps<T>): TeactJsx {
  const [isMenuOpen, openMenu, hideMenu] = useFlag();
  const { isRtl } = useLang();
  const triggerRef = useRef<HTMLDivElement>();
  const menuRef = useRef<HTMLDivElement>();
  const openRequestRef = useRef(0);
  const [menuAnchor, setMenuAnchor] = useState<IAnchorPosition>();
  const withMenu = items.length > 1;
  const isFullyInteractive = Boolean(label);
  const physicalPositionX = isRtl ? menuPositionX === 'right' ? 'left' : 'right' : menuPositionX;

  const getTriggerElement = useLastCallback(() => triggerRef.current?.querySelector('button'));
  const getRootElement = useLastCallback(() => document.body);
  const getMenuElement = useLastCallback(() => menuRef.current);
  const getLayout = useLastCallback((): Layout => ({
    withPortal: true,
    doNotCoverTrigger: true,
    preferredPositionX: physicalPositionX,
    preferredPositionY: menuPositionY,
  }));
  const closeMenu = useLastCallback(() => {
    openRequestRef.current += 1;
    hideMenu();
  });
  const openPortalMenu = useLastCallback((trigger: HTMLElement, anchor: IAnchorPosition, request: number) => {
    if (disabled || !withPortal || request !== openRequestRef.current
      || !trigger.isConnected || trigger !== getTriggerElement()) return;
    setMenuAnchor(anchor);
    openMenu();
  });
  const handleOpenMenu = useLastCallback(() => {
    if (disabled) return;
    if (withPortal) {
      const request = ++openRequestRef.current;
      requestForcedReflow(() => {
        const trigger = getTriggerElement();
        if (!trigger?.isConnected || request !== openRequestRef.current) return undefined;
        const rect = trigger.getBoundingClientRect();
        const anchor = { x: physicalPositionX === 'left' ? rect.left : rect.right, y: rect.bottom };
        return () => openPortalMenu(trigger, anchor, request);
      });
      return;
    }
    openMenu();
  });

  useEffect(() => {
    if (!withPortal || !isMenuOpen) return undefined;
    const handleScroll = (event: Event) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      closeMenu();
    };
    window.addEventListener('resize', closeMenu);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', closeMenu);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [withPortal, isMenuOpen, closeMenu]);

  const selectedItem = useMemo<DropdownItem<T>>(() => {
    let item = selectedValue !== undefined && items.find((i) => i.value === selectedValue);
    item ||= { value: '' as T, name: '' };
    return {
      ...item,
      isDisabled: !isFullyInteractive && disabled,
    };
  }, [items, selectedValue, isFullyInteractive, disabled]);

  const handleSelect = useLastCallback((value: T) => {
    if (value !== selectedValue) {
      onChange?.(value);
    }
  });

  if (!items.length) {
    return undefined;
  }

  const fullClassName = buildClassName(
    className,
    withMenu && styles.interactive,
    isFullyInteractive && styles.wide,
    isFullyInteractive && disabled && styles.disabled,
  );
  const buttonFullClassName = buildClassName(
    styles.button,
    withMenu && styles.interactive,
    withMenu && menuClassName,
    theme && styles[theme],
    itemClassName,
    buttonClassName,
  );

  const buttonSuffix = useMemo(() => {
    return withMenu && (
      <i
        className={buildClassName(styles.buttonIcon, ARROW_ICONS[arrow])}
        aria-hidden
      />
    );
  }, [withMenu, arrow]);

  return (
    <div
      ref={triggerRef}
      className={fullClassName}
      onClick={isFullyInteractive && !disabled && withMenu ? handleOpenMenu : undefined}
    >
      {label && <span className={buildClassName(styles.label, labelClassName)}>{label}</span>}

      {isLoading ? (
        <Spinner className={styles.spinner} />
      ) : (
        <DropdownItemContent
          item={selectedItem}
          prefix={buttonPrefix}
          suffix={buttonSuffix}
          shouldTranslate={shouldTranslateOptions}
          shouldUseSelectedName
          className={buttonFullClassName}
          iconClassName={buttonIconClassName}
          iconOverlayClassName={buttonIconOverlayClassName}
          itemClassName={buildClassName('itemName', itemClassName)}
          onClick={!isFullyInteractive && withMenu ? handleOpenMenu : undefined}
        />
      )}

      {withMenu && (
        <DropdownMenu
          ref={menuRef}
          isOpen={isMenuOpen && !isLoading}
          withPortal={withPortal}
          menuAnchor={withPortal ? menuAnchor : undefined}
          getTriggerElement={withPortal ? getTriggerElement : undefined}
          getRootElement={withPortal ? getRootElement : undefined}
          getMenuElement={withPortal ? getMenuElement : undefined}
          getLayout={withPortal ? getLayout : undefined}
          bubbleClassName={withPortal ? styles.portalMenu : undefined}
          menuPositionX={menuPositionX}
          menuPositionY={menuPositionY}
          withCustomScrollbar={withCustomScrollbar}
          items={items}
          shouldTranslateOptions={shouldTranslateOptions}
          selectedValue={selectedValue}
          buttonClassName={itemClassName}
          onSelect={handleSelect}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

export default memo(Dropdown);
